<template>
  <div class="kanban-bg bg-n-slate-2" aria-hidden="true"></div>
  <div class="kanban-board-container bg-n-slate-2">
    <div class="page-header bg-n-slate-2">
      <h1 class="page-title text-n-slate-12">
        {{ $t('KANBAN.HEADER.TITLE') }}
      </h1>
    </div>

    <div
      class="kanban-content-wrapper bg-n-slate-2"
      :class="{ 'sidebar-collapsed': isSidebarCollapsed }"
    >
      <!-- Sidebar with label filters -->
      <aside
        class="kanban-sidebar bg-n-slate-1 text-n-slate-12"
        :class="{ collapsed: isSidebarCollapsed }"
      >
        <!-- Inbox filter section -->
        <div v-if="!isSidebarCollapsed" class="filter-section">
          <div class="sidebar-header border-n-slate-6">
            <h3 class="sidebar-title text-n-slate-12">
              {{ $t('KANBAN.FILTERS.INBOX') }}
            </h3>
          </div>
          <select
            v-model="selectedInboxId"
            class="inbox-select bg-n-slate-3 text-n-slate-12 border-n-slate-6 hover:border-n-iris-9"
            @change="onInboxChange(selectedInboxId)"
          >
            <option :value="null">
              {{ $t('KANBAN.FILTERS.ALL_INBOXES') }}
            </option>
            <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
              {{ inbox.name }}
            </option>
          </select>
        </div>

        <!-- Label filter section -->
        <div v-if="!isSidebarCollapsed" class="filter-section">
          <Draggable
            v-model="orderedLabelsForSidebar"
            class="label-filters"
            item-key="id"
            :animation="150"
            ghost-class="ghost-label"
            drag-class="dragging-label"
            handle=".label-drag-handle"
            @change="onSidebarOrderChange"
          >
            <template #item="{ element: label }">
              <label
                :key="label.id"
                class="label-filter-item hover:bg-n-slate-3"
              >
                <button
                  type="button"
                  class="label-drag-handle text-n-slate-10 hover:text-n-slate-12"
                  @mousedown.stop
                  @click.stop
                >
                  <fluent-icon icon="drag" size="15" />
                </button>
                <input
                  type="checkbox"
                  :checked="selectedLabelIds.includes(label.id)"
                  @change="toggleLabelVisibility(label.id)"
                />
                <span
                  class="label-color"
                  :style="{ backgroundColor: label.color }"
                ></span>
                <span class="label-name text-n-slate-12">{{
                  label.title
                }}</span>
                <span class="label-count text-n-slate-12 bg-n-slate-5">{{
                  labelTotals[label.id] || 0
                }}</span>
              </label>
            </template>
          </Draggable>
        </div>
      </aside>

      <!-- Kanban board -->
      <div
        ref="kanbanBoard"
        class="kanban-board-wrapper bg-n-slate-2"
        :class="{ 'is-scrolling': isScrolling }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @pointerleave="onPointerLeave"
      >
        <Draggable
          v-model="orderedVisibleLabels"
          class="kanban-board"
          role="list"
          aria-label="Quadro Kanban"
          item-key="id"
          :group="{ name: 'kanban-columns', pull: true, put: false }"
          :animation="200"
          ghost-class="ghost-column"
          drag-class="dragging-column"
          handle=".column-header"
          @change="onColumnDragChange"
        >
          <template #item="{ element: label }">
            <div
              :key="label.id"
              class="kanban-column bg-n-slate-1 border border-n-slate-6"
              role="region"
              :aria-label="`Coluna ${label.title}`"
            >
              <div class="column-header bg-n-slate-2 border-n-slate-6">
                <div
                  class="column-bar"
                  :style="{ backgroundColor: label.color }"
                ></div>
                <div class="column-header-left">
                  <fluent-icon
                    icon="arrows-all"
                    size="16"
                    class="grip-icon text-n-slate-10"
                  />
                  <h3
                    class="column-title text-n-slate-12"
                    :id="`col-${label.id}`"
                  >
                    {{ label.title }}
                  </h3>
                </div>
                <span
                  class="item-count bg-n-slate-5 text-n-slate-12"
                  aria-live="polite"
                  >{{ labelTotals[label.id] || 0 }}</span
                >
              </div>

              <Draggable
                v-if="columnItems[label.id]"
                v-model="columnItems[label.id]"
                class="column-content"
                :group="{
                  name: 'kanban-items',
                  pull: true,
                  put: ['kanban-items'],
                }"
                item-key="uniqueKey"
                :animation="200"
                ghost-class="ghost-card"
                drag-class="dragging-card"
                role="list"
                :aria-labelledby="`col-${label.id}`"
                @start="onCardDragStart"
                @end="onCardDragEnd"
                @change="onDragChange($event, label.id)"
                @scroll.native="
                  handleColumnScroll($event, label.id, label.title)
                "
              >
                <template #item="{ element: item }">
                  <div
                    class="kanban-card bg-n-slate-1 border border-n-slate-6 hover:border-n-iris-9"
                    role="listitem"
                    tabindex="0"
                    @click="onCardClick(item.conversation)"
                  >
                    <div class="card-header">
                      <h4 class="card-title text-n-slate-12">
                        {{ item.title }}
                      </h4>
                      <span
                        class="status-badge"
                        :class="`status-${item.conversation?.status || 'open'}`"
                      >
                        {{
                          getStatusLabel(item.conversation?.status || 'open')
                        }}
                      </span>
                    </div>
                    <p class="card-description text-n-slate-11">
                      {{ item.description }}
                    </p>
                    <div class="card-footer border-n-slate-6">
                      <span
                        v-if="item.conversation?.meta?.assignee"
                        class="assignee text-n-slate-11"
                      >
                        👤 {{ item.conversation.meta.assignee.name }}
                      </span>
                      <span class="timestamp text-n-slate-11">
                        {{
                          formatTimestamp(item.conversation?.last_activity_at)
                        }}
                      </span>
                    </div>
                  </div>
                </template>
              </Draggable>

              <!-- Loading indicator for auto-load -->
              <div v-if="labelLoading[label.id]" class="loading-indicator">
                <Spinner size="small" />
                <span class="text-n-slate-11">Carregando mais...</span>
              </div>

              <!-- Empty state for columns with no conversations -->
              <div
                v-if="
                  !chatListLoading &&
                  !columnItems[label.id]?.length &&
                  !labelHasMore[label.id]
                "
                class="empty-state"
              >
                <p class="empty-state-text text-n-slate-11">
                  {{ $t('KANBAN.BOARD.EMPTY_STATE') }}
                </p>
              </div>
            </div>
          </template>
        </Draggable>

        <!-- Global loading and pagination controls -->
        <div v-if="chatListLoading" class="global-loading">
          <Spinner />
          <p class="loading-text text-n-slate-11">
            {{ $t('KANBAN.BOARD.LOADING') }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Draggable from 'vuedraggable';
import ConversationApi from '../../../../api/conversations';
import KanbanApi from '../../../../api/kanban';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';

export default {
  name: 'KanbanBoard',
  components: {
    Draggable,
    Spinner,
  },
  data() {
    return {
      // Conversation filters
      conversationFilters: {
        assigneeType: 'all',
        status: 'open',
        page: 1,
        perPage: 50, // Control per_page directly
        perLabel: 10, // Number of conversations to fetch per label on initial load
        isInitialLoad: true, // Flag to trigger balanced loading by label
      },
      // Drag-to-scroll state
      isScrolling: false,
      dragStartX: 0,
      dragScrollStart: 0,
      dragPointerId: null,
      bodyUserSelectBackup: '',
      // Label filter state
      selectedLabelIds: [],
      // Custom column order for drag and drop
      customColumnOrder: [], // Array of label IDs in custom order
      orderedVisibleLabels: [], // Reactive array for v-model binding (stable reference)
      // Inbox filter state
      selectedInboxId: null,
      // Local state for conversations
      conversations: [],
      chatListLoading: false,
      hasEndReachedAll: false,
      totalCount: 0,
      // Per-label pagination state (cursor-based)
      labelCursors: {}, // { labelId: "timestamp_id" } - cursor for next page
      labelHasMore: {}, // { labelId: boolean }
      labelLoading: {}, // { labelId: boolean }
      labelTotals: {}, // { labelId: totalCount } - total conversations per label from backend
      // Sidebar state
      isSidebarCollapsed: false,
      orderedLabelsForSidebar: [],
      columnItems: {},
      // Card click vs drag tracking
      isDraggingCard: false,
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
      inboxes: 'inboxes/getInboxes',
    }),
    visibleLabels() {
      if (this.selectedLabelIds.length === 0) {
        return [];
      }

      // Filter labels based on selection
      const filtered = this.labels.filter(label =>
        this.selectedLabelIds.includes(label.id)
      );

      // Sort by custom order
      return filtered.sort((a, b) => {
        const indexA = this.customColumnOrder.indexOf(a.id);
        const indexB = this.customColumnOrder.indexOf(b.id);

        // If both exist in custom order, sort by that
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }

        // If only one exists, prioritize it
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;

        // If neither exists (shouldn't happen), maintain original order
        return 0;
      });
    },
  },
  watch: {
    // Sync orderedVisibleLabels whenever filters or order changes
    visibleLabels: {
      handler(newVisibleLabels) {
        // Only update if the content actually changed (not just reference)
        // This prevents unnecessary updates during drag operations
        const newIds = newVisibleLabels.map(l => l.id).join(',');
        const currentIds = this.orderedVisibleLabels.map(l => l.id).join(',');

        if (newIds !== currentIds) {
          this.orderedVisibleLabels = [...newVisibleLabels];
        }
      },
      immediate: true, // Run on mount
    },
    labels: {
      handler() {
        this.syncColumnOrderWithLabels();
        this.ensureSelectedLabelsValid();
        this.syncOrderedLabelsForSidebar();
        this.buildColumnItems();
      },
      deep: true,
      immediate: true,
    },
    conversations: {
      handler() {
        this.buildColumnItems();
      },
      deep: true,
    },
  },
  async mounted() {
    // Fetch labels, inboxes, and conversations from the store
    await this.$store.dispatch('labels/get');
    await this.$store.dispatch('inboxes/get');
    this.initializeSidebarState();
    this.initializeLabelSelection();
    this.initializeInboxSelection();
    this.initializeColumnOrder();
    this.initializeLabelPagination();
    this.updateFiltersAndFetch();
  },
  methods: {
    syncColumnOrderWithLabels({ preferSaved = false } = {}) {
      if (!Array.isArray(this.labels) || this.labels.length === 0) {
        this.customColumnOrder = [];
        this.orderedLabelsForSidebar = [];
        this.columnItems = {};
        return;
      }

      const labelIds = this.labels.map(label => label.id);
      let baseOrder = [...this.customColumnOrder];

      if (preferSaved) {
        const saved = localStorage.getItem('kanban_column_order');
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
              baseOrder = parsed;
            }
          } catch (error) {
            baseOrder = [];
          }
        }
      }

      const uniqueBaseOrder = Array.isArray(baseOrder)
        ? baseOrder.filter((id, index) => baseOrder.indexOf(id) === index)
        : [];
      const sanitizedOrder = uniqueBaseOrder.filter(id =>
        labelIds.includes(id)
      );

      const missingIds = labelIds.filter(id => !sanitizedOrder.includes(id));
      const mergedOrder = [...sanitizedOrder, ...missingIds];

      if (!mergedOrder.length) {
        mergedOrder.push(...labelIds);
      }

      if (mergedOrder.join(',') !== this.customColumnOrder.join(',')) {
        this.customColumnOrder = mergedOrder;
      }
    },
    syncOrderedLabelsForSidebar() {
      if (!Array.isArray(this.labels) || !this.labels.length) {
        this.orderedLabelsForSidebar = [];
        return;
      }

      if (!this.customColumnOrder.length) {
        const allLabels = [...this.labels];
        const currentIds = this.orderedLabelsForSidebar
          .map(label => label.id)
          .join(',');
        const newIds = allLabels.map(label => label.id).join(',');
        if (currentIds !== newIds) {
          this.orderedLabelsForSidebar = allLabels;
        }
        return;
      }

      const labelMap = new Map(this.labels.map(label => [label.id, label]));
      const ordered = this.customColumnOrder
        .map(id => labelMap.get(id))
        .filter(Boolean);
      const missing = this.labels.filter(
        label => !this.customColumnOrder.includes(label.id)
      );

      const combined = [...ordered, ...missing];
      const currentIds = this.orderedLabelsForSidebar
        .map(label => label.id)
        .join(',');
      const nextIds = combined.map(label => label.id).join(',');

      if (currentIds !== nextIds) {
        this.orderedLabelsForSidebar = combined;
      }
    },
    ensureSelectedLabelsValid() {
      if (!Array.isArray(this.labels) || !this.labels.length) {
        this.selectedLabelIds = [];
        return;
      }

      const validIds = this.labels.map(label => label.id);
      const filteredSelection = this.selectedLabelIds.filter(id =>
        validIds.includes(id)
      );
      const dedupedSelection = [...new Set(filteredSelection)];

      if (!dedupedSelection.length) {
        this.selectedLabelIds = [...validIds];
        return;
      }

      if (dedupedSelection.join(',') !== this.selectedLabelIds.join(',')) {
        this.selectedLabelIds = dedupedSelection;
      }
    },
    buildColumnItems() {
      if (!Array.isArray(this.labels) || !this.labels.length) {
        this.columnItems = {};
        return;
      }

      const labelTitleMap = new Map(
        this.labels.map(label => [label.title, label])
      );
      const columns = this.labels.reduce((acc, label) => {
        acc[label.id] = [];
        return acc;
      }, {});

      this.conversations.forEach(conversation => {
        const { id, meta, labels = [] } = conversation;
        const contactName = meta?.sender?.name || 'Unknown';
        const lastMessage =
          conversation.messages?.[conversation.messages.length - 1];
        const description = (lastMessage?.content || 'No messages').substring(
          0,
          100
        );

        labels.forEach(labelTitle => {
          const labelObj = labelTitleMap.get(labelTitle);
          if (!labelObj) {
            return;
          }

          const labelId = labelObj.id;
          columns[labelId].push({
            id,
            labelId,
            uniqueKey: `${id}-${labelId}`,
            title: contactName,
            description,
            conversation,
          });
        });
      });

      this.labels.forEach(label => {
        columns[label.id].sort((a, b) => {
          const positionA =
            a.conversation?.kanban_positions?.[label.id] ?? undefined;
          const positionB =
            b.conversation?.kanban_positions?.[label.id] ?? undefined;

          // Both have positions: sort by position value
          if (positionA !== undefined && positionB !== undefined) {
            return positionA - positionB;
          }
          // One has position: positioned items come first
          if (positionA !== undefined) {
            return -1;
          }
          if (positionB !== undefined) {
            return 1;
          }

          // Neither has position: preserve original loading order from conversations array
          const indexA = this.conversations.findIndex(
            c => c.id === a.conversation.id
          );
          const indexB = this.conversations.findIndex(
            c => c.id === b.conversation.id
          );
          return indexA - indexB;
        });
      });

      this.columnItems = columns;
    },
    onSidebarOrderChange() {
      this.customColumnOrder = this.orderedLabelsForSidebar.map(
        label => label.id
      );
      this.saveColumnOrderToLocalStorage();
      this.syncOrderedLabelsForSidebar();
    },
    toggleSidebarCollapse() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
      this.saveSidebarStateToLocalStorage();
    },
    initializeSidebarState() {
      const saved = localStorage.getItem('kanban_sidebar_collapsed');
      if (saved !== null) {
        try {
          this.isSidebarCollapsed = JSON.parse(saved);
        } catch (error) {
          this.isSidebarCollapsed = false;
        }
      }
    },
    saveSidebarStateToLocalStorage() {
      localStorage.setItem(
        'kanban_sidebar_collapsed',
        JSON.stringify(this.isSidebarCollapsed)
      );
    },
    getStatusLabel(status) {
      const statusKey = status.toUpperCase();
      return this.$t(`KANBAN.STATUS.${statusKey}`) || status;
    },
    async onDragChange(event, labelId) {
      // Handle cross-column drag (added to new column)
      if (event.added) {
        const movedItem = event.added.element;
        if (!movedItem || !movedItem.conversation) {
          // Elemento inválido inserido (ex.: coluna); reconstroi o estado e cancela o drop.
          this.$nextTick(() => {
            this.buildColumnItems();
          });
          return;
        }
        const newIndex = event.added.newIndex;
        const conversation = movedItem.conversation;

        // Find the new label object by ID (destination column)
        const newLabel = this.labels.find(l => l.id === labelId);
        if (!newLabel) return;

        // Find the old label object by ID (source column)
        const oldLabel = this.labels.find(l => l.id === movedItem.labelId);

        try {
          // Update local item reference to new label
          movedItem.labelId = labelId;

          // Get current labels
          const currentLabels = conversation.labels || [];

          // Remove old label (if it exists) and add new label
          let updatedLabels;
          if (oldLabel) {
            // Remove the old label and add the new one
            updatedLabels = currentLabels
              .filter(labelTitle => labelTitle !== oldLabel.title)
              .concat(newLabel.title);
          } else {
            // No old label, just add the new one
            updatedLabels = [...currentLabels, newLabel.title];
          }

          // Remove duplicates (in case the new label was already in the list)
          updatedLabels = [...new Set(updatedLabels)];

          // Optimistically update local state FIRST for instant UI feedback (before API call)
          const conversationIndex = this.conversations.findIndex(
            c => c.id === conversation.id
          );
          if (conversationIndex !== -1) {
            this.conversations[conversationIndex].labels = updatedLabels;

            // Initialize kanban_positions if it doesn't exist
            if (!this.conversations[conversationIndex].kanban_positions) {
              this.conversations[conversationIndex].kanban_positions = {};
            }

            // Remove position from old column (if there was one)
            if (oldLabel) {
              delete this.conversations[conversationIndex].kanban_positions[
                oldLabel.id
              ];
            }
          }

          // Then update via API (use display_id for the API endpoint)
          await ConversationApi.updateLabels(
            conversation.display_id,
            updatedLabels
          );

          // Recalculate positions for all cards in the new column to avoid conflicts
          this.$nextTick(() => {
            this.recalculateColumnPositions(labelId);
            if (oldLabel) {
              this.recalculateColumnPositions(oldLabel.id);
            }
          });

          if (process.env.NODE_ENV === 'development') {
            console.log(
              `Conversation ${conversation.id} moved from ${oldLabel?.title || 'unlabeled'} to ${newLabel.title} at position ${newIndex}`
            );
          }
        } catch (error) {
          console.error('Error updating conversation label:', error);
          // TODO: Add proper error notification
        }
      }

      // Handle same-column reordering (moved within same column)
      if (event.moved) {
        try {
          // Recalculate positions for all cards in the column
          this.$nextTick(() => {
            this.recalculateColumnPositions(labelId);
          });

          console.log(`Conversation reordered in column ${labelId}`);
        } catch (error) {
          console.error('Error updating conversation position:', error);
          // TODO: Add proper error notification
        }
      }
    },
    onColumnDragChange(event) {
      // Update customColumnOrder based on the new order from vuedraggable
      if (event.moved) {
        // Extract the new order from orderedVisibleLabels
        this.customColumnOrder = this.orderedVisibleLabels.map(
          label => label.id
        );

        // Add any labels not currently visible (maintain their relative order)
        const hiddenLabelIds = this.labels
          .filter(label => !this.selectedLabelIds.includes(label.id))
          .map(label => label.id);

        // Preserve position of hidden labels in customColumnOrder
        hiddenLabelIds.forEach(hiddenId => {
          if (!this.customColumnOrder.includes(hiddenId)) {
            this.customColumnOrder.push(hiddenId);
          }
        });

        // Save to localStorage
        this.saveColumnOrderToLocalStorage();
        this.syncOrderedLabelsForSidebar();

        console.log('Column order updated:', this.customColumnOrder);
      }
    },
    shouldStartSurfaceDrag(event) {
      const board = this.$refs.kanbanBoard;
      if (!board || !(event.target instanceof Element)) {
        return false;
      }

      if (event.pointerType === 'mouse' && event.button !== 0) {
        return false;
      }

      const disallowedSelectors = [
        '.kanban-card',
        '.column-header',
        '.label-filter-item',
        '.label-drag-handle',
        '.load-more-btn',
        'button',
        'a',
        'input',
        'textarea',
        'select',
      ];

      if (
        disallowedSelectors.some(selector => event.target.closest(selector))
      ) {
        return false;
      }

      const isBoardSurface =
        event.target === board ||
        event.target.classList.contains('kanban-board-wrapper') ||
        event.target.classList.contains('kanban-board') ||
        event.target.classList.contains('column-content') ||
        event.target.closest('.kanban-board') === board;

      return isBoardSurface;
    },
    onPointerDown(event) {
      if (!this.shouldStartSurfaceDrag(event)) {
        return;
      }

      const board = this.$refs.kanbanBoard;
      if (!board) {
        return;
      }

      this.isScrolling = true;
      this.dragStartX = event.clientX;
      this.dragScrollStart = board.scrollLeft;
      this.dragPointerId =
        typeof event.pointerId === 'number' ? event.pointerId : null;

      if (typeof document !== 'undefined') {
        this.bodyUserSelectBackup = document.body.style.userSelect;
        document.body.style.userSelect = 'none';
      }

      if (this.dragPointerId !== null && board.setPointerCapture) {
        try {
          board.setPointerCapture(this.dragPointerId);
        } catch (error) {
          // Pointer capture might not be supported; ignore silently.
        }
      }

      event.preventDefault();
    },
    onPointerMove(event) {
      if (!this.isScrolling) {
        return;
      }

      if (
        this.dragPointerId !== null &&
        typeof event.pointerId === 'number' &&
        event.pointerId !== this.dragPointerId
      ) {
        return;
      }

      const board = this.$refs.kanbanBoard;
      if (!board) {
        return;
      }

      event.preventDefault();
      const deltaX = event.clientX - this.dragStartX;
      board.scrollLeft = this.dragScrollStart - deltaX;
    },
    onPointerUp(event) {
      if (!this.isScrolling) {
        return;
      }

      if (
        this.dragPointerId !== null &&
        typeof event.pointerId === 'number' &&
        event.pointerId !== this.dragPointerId
      ) {
        return;
      }

      const board = this.$refs.kanbanBoard;
      if (board && this.dragPointerId !== null && board.releasePointerCapture) {
        try {
          board.releasePointerCapture(this.dragPointerId);
        } catch (error) {
          // If pointer capture wasn't set, ignore the error.
        }
      }

      this.isScrolling = false;
      this.dragPointerId = null;
      if (typeof document !== 'undefined') {
        document.body.style.userSelect = this.bodyUserSelectBackup || '';
        this.bodyUserSelectBackup = '';
      }
    },
    onPointerLeave(event) {
      if (!this.isScrolling) {
        return;
      }
      this.onPointerUp(event);
    },
    handleColumnScroll(event, labelId, labelTitle) {
      const scrollTop = event.target.scrollTop;
      const scrollHeight = event.target.scrollHeight;
      const clientHeight = event.target.clientHeight;
      const scrollPercentage = (
        (scrollTop / (scrollHeight - clientHeight)) *
        100
      ).toFixed(2);

      // Auto-load more when scrolled to 80% or higher
      if (parseFloat(scrollPercentage) >= 90) {
        // Check if we can load more and not already loading
        if (
          this.labelHasMore[labelId] &&
          !this.labelLoading[labelId] &&
          !this.chatListLoading
        ) {
          console.log(
            `[Kanban Auto-Load] Triggering load more for "${labelTitle}" (ID: ${labelId})`
          );
          this.loadMoreForLabel(labelId);
        }
      }
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp * 1000); // Convert Unix timestamp to milliseconds
      const now = new Date();
      const diff = now - date;

      // Less than 1 minute
      if (diff < 60000) {
        return this.$t('KANBAN.TIMESTAMP.JUST_NOW');
      }
      // Less than 1 hour
      if (diff < 3600000) {
        const minutes = Math.floor(diff / 60000);
        return this.$t('KANBAN.TIMESTAMP.MINUTES_AGO', { minutes });
      }
      // Less than 24 hours
      if (diff < 86400000) {
        const hours = Math.floor(diff / 3600000);
        return this.$t('KANBAN.TIMESTAMP.HOURS_AGO', { hours });
      }
      // Less than 7 days
      if (diff < 604800000) {
        const days = Math.floor(diff / 86400000);
        return this.$t('KANBAN.TIMESTAMP.DAYS_AGO', { days });
      }
      // Format as date
      return date.toLocaleDateString();
    },
    async loadMoreConversations() {
      // Don't load if already loading or end reached
      if (this.chatListLoading || this.hasEndReachedAll) {
        return;
      }

      // Increment page and fetch more conversations
      this.conversationFilters.page += 1;
      await this.fetchConversations(true); // append = true
    },
    toggleLabelVisibility(labelId) {
      const index = this.selectedLabelIds.indexOf(labelId);
      if (index === -1) {
        this.selectedLabelIds.push(labelId);
      } else {
        this.selectedLabelIds.splice(index, 1);
      }
      this.saveLabelSelectionToLocalStorage();
    },

    initializeLabelSelection() {
      // Try to load from localStorage first
      const saved = localStorage.getItem('kanban_selected_labels');
      const allLabelIds = this.labels.map(label => label.id);

      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          if (Array.isArray(parsed)) {
            const filtered = [...new Set(parsed)].filter(id =>
              allLabelIds.includes(id)
            );
            this.selectedLabelIds =
              filtered.length > 0 ? filtered : [...allLabelIds];
            return;
          }
        } catch (error) {
          // Fallback handled below
        }
      }

      // Default: select all labels
      this.selectedLabelIds = [...allLabelIds];
    },
    saveLabelSelectionToLocalStorage() {
      localStorage.setItem(
        'kanban_selected_labels',
        JSON.stringify(this.selectedLabelIds)
      );
    },
    initializeColumnOrder() {
      this.syncColumnOrderWithLabels({ preferSaved: true });
      this.syncOrderedLabelsForSidebar();
      this.buildColumnItems();
    },
    saveColumnOrderToLocalStorage() {
      localStorage.setItem(
        'kanban_column_order',
        JSON.stringify(this.customColumnOrder)
      );
    },
    initializeInboxSelection() {
      // Try to load from localStorage first
      const saved = localStorage.getItem('kanban_selected_inbox');
      if (saved) {
        try {
          this.selectedInboxId = JSON.parse(saved);
        } catch (e) {
          // If parsing fails, set to null (all inboxes)
          this.selectedInboxId = null;
        }
      } else {
        // Default to null (all inboxes)
        this.selectedInboxId = null;
      }
    },
    initializeLabelPagination() {
      const cursors = {};
      const hasMore = {};
      const loading = {};

      this.labels.forEach(label => {
        cursors[label.id] = '';
        hasMore[label.id] = true;
        loading[label.id] = false;
      });

      this.labelCursors = cursors;
      this.labelHasMore = hasMore;
      this.labelLoading = loading;
    },
    saveInboxSelectionToLocalStorage() {
      localStorage.setItem(
        'kanban_selected_inbox',
        JSON.stringify(this.selectedInboxId)
      );
    },
    onInboxChange(inboxId) {
      this.selectedInboxId = inboxId;
      this.saveInboxSelectionToLocalStorage();
      // Reset pagination and refetch conversations
      this.conversationFilters.page = 1;
      this.updateFiltersAndFetch();
    },
    async updateFiltersAndFetch() {
      // Reset to page 1 and clear conversations
      this.initializeLabelPagination();
      this.conversationFilters.page = 1;
      this.conversationFilters.isInitialLoad = true; // Reset to initial load mode
      this.conversations = [];
      this.hasEndReachedAll = false;
      this.buildColumnItems();

      // Fetch new conversations
      await this.fetchConversations(false);
    },
    mergeConversations(newItems, { replace = false } = {}) {
      const items = Array.isArray(newItems) ? newItems : [];

      if (replace) {
        const existingMap = new Map(
          this.conversations.map(conversation => [
            conversation.id,
            conversation,
          ])
        );
        const mergedMap = new Map();

        items.forEach(item => {
          const existing = existingMap.get(item.id);
          if (existing) {
            mergedMap.set(item.id, {
              ...existing,
              ...item,
              kanban_positions:
                existing.kanban_positions ?? item.kanban_positions,
            });
          } else {
            mergedMap.set(item.id, item);
          }
        });

        this.conversations = Array.from(mergedMap.values());
        return;
      }

      if (!items.length) {
        return;
      }

      // When appending (replace=false), keep existing conversations first
      // and add new ones at the end to maintain loading order
      const existingMap = new Map(
        this.conversations.map(conversation => [conversation.id, conversation])
      );

      // Start with existing conversations in their current order
      const result = [...this.conversations];

      // Add new items that don't already exist to the end
      items.forEach(item => {
        const existing = existingMap.get(item.id);

        if (existing) {
          // Update existing item in place
          const index = result.findIndex(conv => conv.id === item.id);
          if (index !== -1) {
            result[index] = {
              ...existing,
              ...item,
              kanban_positions:
                existing.kanban_positions ?? item.kanban_positions,
            };
          }
        } else {
          // Add new item to the end
          result.push(item);
        }
      });

      this.conversations = result;
    },
    async fetchConversations(append = false) {
      try {
        this.chatListLoading = true;

        const accountId = this.$store.state.auth.currentUser?.account_id;
        if (!accountId) {
          console.error('No account ID found');
          return;
        }

        const response = await KanbanApi.getConversations({
          accountId,
          perPage: this.conversationFilters.perPage,
          page: this.conversationFilters.page,
          status: this.conversationFilters.status,
          assigneeType: this.conversationFilters.assigneeType,
          inboxId: this.selectedInboxId,
          perLabel: this.conversationFilters.perLabel,
          isInitialLoad: this.conversationFilters.isInitialLoad,
        });

        if (response.data.success) {
          const { payload, meta } = response.data.data;

          // Append or replace conversations
          this.mergeConversations(payload, { replace: !append });
          this.buildColumnItems();

          // Update pagination state
          this.totalCount = meta.total_count;
          this.hasEndReachedAll = !meta.has_next_page;

          // Store label totals from backend
          if (meta.label_totals) {
            this.labelTotals = meta.label_totals;
          }

          // After first load, switch to normal pagination mode
          if (this.conversationFilters.isInitialLoad) {
            this.conversationFilters.isInitialLoad = false;
          }
        }
      } catch (error) {
        console.error('Error fetching kanban conversations:', error);
        // TODO: Add proper error notification
      } finally {
        this.chatListLoading = false;
      }
    },
    async loadMoreForLabel(labelId) {
      // Don't load if already loading for this label
      if (this.labelLoading[labelId] || !this.labelHasMore[labelId]) {
        return;
      }

      try {
        this.labelLoading[labelId] = true;

        const accountId = this.$store.state.auth.currentUser?.account_id;
        if (!accountId) {
          console.error('No account ID found');
          return;
        }

        const labelExists = this.labels.some(l => l.id === labelId);
        if (!labelExists) {
          console.error('Label not found');
          return;
        }

        const maxRequestsPerScroll = 5;
        let requestCount = 0;

        let cursor = this.labelCursors[labelId] ?? '';
        let hasMore = this.labelHasMore[labelId];

        const loadNextPage = async () => {
          if (!hasMore || requestCount >= maxRequestsPerScroll) {
            return;
          }

          requestCount += 1;
          const previousCount = this.columnItems?.[labelId]?.length || 0;

          const response = await KanbanApi.getConversations({
            accountId,
            perPage: this.conversationFilters.perLabel,
            status: this.conversationFilters.status,
            assigneeType: this.conversationFilters.assigneeType,
            inboxId: this.selectedInboxId,
            labelId: labelId,
            cursor: cursor ?? '',
          });

          if (!response.data.success) {
            return;
          }

          const { payload, meta } = response.data.data;

          this.mergeConversations(payload);
          this.buildColumnItems();

          hasMore = meta.has_next_page;
          cursor = meta.next_cursor ?? null;
          this.labelHasMore[labelId] = hasMore;
          this.labelCursors[labelId] = cursor;

          if (meta.label_totals) {
            this.labelTotals = { ...this.labelTotals, ...meta.label_totals };
          }

          const nextCount = this.columnItems?.[labelId]?.length || 0;
          if (nextCount > previousCount || !hasMore || !cursor) {
            return;
          }

          await loadNextPage();
        };

        await loadNextPage();
      } catch (error) {
        console.error(`Error loading more for label ${labelId}:`, error);
        // TODO: Add proper error notification
      } finally {
        this.labelLoading[labelId] = false;
      }
    },
    recalculateColumnPositions(labelId) {
      // Get current items in the column (after drag has completed)
      const items = this.columnItems[labelId];
      if (!Array.isArray(items)) {
        return;
      }

      // Update position for each item based on its current index
      items.forEach((item, index) => {
        const conversationIndex = this.conversations.findIndex(
          c => c.id === item.id
        );

        if (conversationIndex !== -1) {
          // Initialize kanban_positions if it doesn't exist
          if (!this.conversations[conversationIndex].kanban_positions) {
            this.conversations[conversationIndex].kanban_positions = {};
          }

          // Set position to current index in the column
          this.conversations[conversationIndex].kanban_positions[labelId] =
            index;
        }
      });
    },
    onCardClick(conversation) {
      // Don't open if the card was being dragged
      if (this.isDraggingCard) {
        return;
      }

      // Don't open if no conversation provided
      if (!conversation) {
        return;
      }

      const accountId = this.$store.state.auth.currentUser?.account_id;
      if (!accountId) {
        console.error('No account ID found');
        return;
      }

      // Use display_id for the URL (this is what the backend expects)
      const conversationId = conversation.display_id || conversation.id;
      if (!conversationId) {
        console.error('No conversation ID found');
        return;
      }

      // Generate the conversation URL
      const path = frontendURL(
        conversationUrl({
          accountId,
          id: conversationId,
        })
      );

      // Open in new tab (without window features to ensure tab vs popup)
      const newTab = window.open(path, '_blank');
      if (newTab) {
        newTab.opener = null; // Security: prevent access to window.opener
      }
    },
    onCardDragStart() {
      // Mark that a card is being dragged
      this.isDraggingCard = true;
    },
    onCardDragEnd() {
      // Reset drag state after a small delay to allow the click event to check this flag
      setTimeout(() => {
        this.isDraggingCard = false;
      }, 100);
    },
  },
};
</script>

<style scoped lang="scss">
.kanban-board-container {
  --space-fluid: clamp(var(--space-medium), 3vw, var(--space-huge));
  padding: 0;
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: 'Manrope', sans-serif;
  margin-right: calc((100vw - 100%) / -2);
}

.kanban-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
  width: 100vw;
  left: 50%;
  transform: translateX(-50%);
}

.page-header {
  padding: var(--space-medium) var(--space-medium) var(--space-small);
}

.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.02em;
}

.kanban-content-wrapper {
  display: flex;
  gap: var(--space-medium);
  flex: 1;
  overflow: hidden;
  padding: 0 var(--space-medium) var(--space-medium);

  &.sidebar-collapsed {
    gap: var(--space-normal);
  }
}

.kanban-sidebar {
  position: relative;
  min-width: 260px;
  max-width: 260px;
  border-radius: 12px;
  padding: var(--space-medium);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--space-medium);
  @apply shadow-md;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    @apply bg-n-slate-3;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-n-slate-8;
    border-radius: 3px;

    &:hover {
      @apply bg-n-slate-9;
    }
  }

  &.collapsed {
    min-width: 56px;
    max-width: 56px;
    padding: var(--space-medium) var(--space-small);
    align-items: center;
    gap: var(--space-small);

    .sidebar-toggle {
      align-self: center;
    }

    .filter-section {
      display: none;
    }
  }
}

.sidebar-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease;
  margin-bottom: var(--space-small);
}

.filter-section {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: var(--space-small);
  border-bottom: 1px solid;
}

.sidebar-title {
  font-size: var(--font-size-default);
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
}

.toggle-all-btn {
  background: transparent;
  border: none;
  font-size: var(--font-size-mini);
  cursor: pointer;
  padding: var(--space-micro) var(--space-small);
  border-radius: 6px;
  transition: all 0.2s ease;
  font-weight: 600;
}

.inbox-select {
  width: 100%;
  padding: var(--space-small) var(--space-normal);
  border: 1px solid;
  border-radius: 8px;
  font-size: var(--font-size-small);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Manrope', sans-serif;

  &:focus {
    outline: none;
    @apply ring-2 ring-n-iris-9;
  }
}

.label-filters {
  display: flex;
  flex-direction: column;
  gap: var(--space-small);
}

.label-filter-item {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  input[type='checkbox'] {
    cursor: pointer;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    border-radius: 4px;
    @apply accent-n-iris-9;
  }

  .label-drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1px;
    border: none;
    background: transparent;
    cursor: grab;
    flex-shrink: 0;
    transition: color 0.2s ease;

    &:active {
      cursor: grabbing;
    }

    svg {
      pointer-events: none;
    }
  }

  .label-color {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .label-name {
    flex: 1;
    font-size: var(--font-size-small);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
  }

  .label-count {
    font-size: 0.8em;
    padding: 2px 4px;
    border-radius: 4px;
    flex-shrink: 0;
    font-weight: 700;
    min-width: 24px;
    text-align: center;
  }
}

.kanban-board-wrapper {
  overflow-x: auto;
  flex: 1;
  user-select: none;

  &.is-scrolling {
    scroll-behavior: auto;
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    @apply bg-n-slate-3;
    border-radius: 5px;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-n-slate-8;
    border-radius: 5px;

    &:hover {
      @apply bg-n-slate-9;
    }
  }
}

.kanban-board {
  display: flex;
  gap: var(--space-medium);
  padding: var(--space-small) 0 var(--space-small);
}

.kanban-column {
  min-width: clamp(240px, 25vw, 300px);
  flex: 1;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  height: fit-content;
  max-height: calc(100vh - 125px);
  pointer-events: auto;
  overflow: hidden;
  @apply shadow-sm;
}

.column-header {
  padding: var(--space-small) var(--space-medium);
  border-bottom: 1px solid;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}

.column-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  border-radius: 12px 0 0 0;
  opacity: 0.9;
}

.column-header-left {
  display: flex;
  align-items: center;
  gap: var(--space-small);
}

.grip-icon {
  flex-shrink: 0;
  opacity: 0.6;
  transition:
    opacity 0.2s ease,
    color 0.2s ease;
}

.column-header:hover .grip-icon {
  opacity: 1;
}

.column-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
}

.item-count {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: var(--font-size-mini);
  font-weight: 700;
  min-width: 28px;
  text-align: center;
}

.column-content {
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  gap: var(--space-normal);
  overflow-y: auto;
  min-height: 80px;
  flex: 1;
  transition: all 0.2s ease;

  &:has(.ghost-card) {
    @apply outline-2 outline-dashed outline-n-slate-6;
    outline-offset: 6px;
    @apply bg-n-slate-2/60;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-n-slate-8;
    border-radius: 3px;

    &:hover {
      @apply bg-n-slate-9;
    }
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-larger);
  min-height: 150px;
}

.empty-state-text {
  font-size: var(--font-size-small);
  text-align: center;
  margin: 0;
  font-weight: 500;
}

.global-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-larger);
  gap: var(--space-normal);
}

.loading-text {
  font-size: var(--font-size-small);
  margin: 0;
  font-weight: 500;
}

.kanban-card {
  border-radius: 10px;
  padding: 12px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  @apply shadow-sm;

  &:hover {
    @apply shadow-lg;
    transform: translateY(-2px);
  }

  &:active {
    cursor: grabbing;
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-small);
  gap: var(--space-small);
}

.status-badge {
  font-size: var(--font-size-mini);
  padding: 4px 10px;
  border-radius: 12px;
  text-transform: uppercase;
  font-weight: 700;
  flex-shrink: 0;
  letter-spacing: 0.02em;

  &.status-open {
    @apply bg-n-iris-3 border border-n-iris-9 text-n-iris-11;
  }

  &.status-resolved {
    @apply bg-n-teal-3 border border-n-teal-9 text-n-teal-11;
  }

  &.status-snoozed,
  &.status-pending {
    @apply bg-n-amber-3 border border-n-amber-9 text-n-amber-11;
  }
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-small);
  padding-top: var(--space-micro);
  border-top: 1px solid;
  font-size: var(--font-size-mini);
  gap: var(--space-small);
}

.assignee {
  display: flex;
  align-items: center;
  gap: var(--space-small);
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.timestamp {
  flex-shrink: 0;
  font-weight: 500;
}

.ghost-card {
  opacity: 0.4;
  @apply bg-n-slate-2;
  @apply border-2 border-dashed border-n-slate-6;
}

.dragging-card {
  opacity: 0.95;
  transform: rotate(3deg);
  @apply shadow-2xl;
}

.ghost-column {
  opacity: 0.3;
  @apply bg-n-slate-5/20;
  @apply border-2 border-dashed border-n-slate-6;
}

.dragging-column {
  opacity: 0.9;
  transform: rotate(1deg) scale(1.02);
  @apply shadow-2xl;
  transition: transform 0.2s ease;
}

.ghost-label {
  opacity: 0.4;
}

.dragging-label {
  cursor: grabbing !important;
  opacity: 0.85;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 var(--space-small) 0;
  pointer-events: none;
  letter-spacing: -0.01em;
  line-height: 1.4;
}

.card-description {
  font-size: var(--font-size-small);
  margin: 0;
  pointer-events: none;
  line-height: 1.6;
}

.load-more-wrapper {
  display: flex;
  justify-content: center;
  padding: var(--space-small);
}

.global-load-more {
  display: flex;
  justify-content: center;
  padding: var(--space-large);
  margin-top: var(--space-medium);
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-medium);
  font-size: var(--font-size-small);
}

.load-more-btn {
  border: none;
  border-radius: 8px;
  padding: var(--space-small) var(--space-medium);
  font-size: var(--font-size-small);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--space-small);
  min-height: 36px;
  font-weight: 700;
  font-family: 'Manrope', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    @apply bg-n-slate-12/10;
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
  }

  &:hover:not(:disabled)::before {
    opacity: 1;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    @apply shadow-md;
  }

  &:focus {
    @apply outline-2 outline-n-iris-9;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.column-loading {
  display: flex;
  justify-content: center;
  padding: var(--space-normal);
}

:focus-visible {
  @apply outline-2 outline-n-iris-9;
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .kanban-card,
  .load-more-btn {
    transition: none;
    transform: none !important;
  }

  .column-content {
    transition: none;
  }
}
</style>
